import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCommissionerActAs } from '@/contexts/CommissionerActAsContext';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import { canPlaySlot, formatPositionDisplay, toArray } from '@/lib/positionEligibility';
import { computePlayerLocks, collectLockedTeamIds } from '@/lib/mlbLineupLocks';
import { todayET } from '@/lib/etDate';

/**
 * SAB-793: the implementation now lives in @/lib/etDate. Kept as a named export so the
 * existing external callers of getTodayET keep working.
 *
 * NOTE: this is an import + alias, NOT `export { todayET as getTodayET } from ...`.
 * A re-export creates no local binding, so the seven internal callers in this file would
 * be `getTodayET is not defined` at RUNTIME. tsc and `vite build` both accepted that;
 * only eslint's no-undef caught it.
 */
export const getTodayET = todayET;

/** MLB status descriptions that qualify a player for IL placement.
 *  Matches both old-style names and MLB API roster descriptions. */
const IL_ELIGIBLE_STATUSES = new Set([
  // MLB API roster status descriptions
  'Injured 10-Day',
  'Injured 15-Day',
  'Injured 60-Day',
  'Injured 7-Day',
  'Injured - Full Season',
  'Paternity Leave',
  'Bereavement',
  'Restricted List',
  // Legacy / alternate names (keep for safety)
  '10-Day Injured List',
  '15-Day Injured List',
  '60-Day Injured List',
  'Day-To-Day',
  '7-Day Injured List',
]);

/** Check if a status qualifies for IL (handles variable-text statuses like "Suspended # days") */
function isILEligibleStatus(status: string): boolean {
  if (IL_ELIGIBLE_STATUSES.has(status)) return true;
  if (status.startsWith('Suspended') || status.startsWith('Injured')) return true;
  return false;
}

export interface RosterPlayer {
  assignment_id: string;
  player_id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  mlb_team_abbreviation: string | null;
  // SAB-751: locks compare TEAM ID against game_schedule instead of translating
  // abbreviation -> name via a 9,149-row players read that silently truncated.
  mlb_team_id: number | null;
  mlb_id: number | null;
  headshot_url: string | null;
  slot: string;
  is_starter: boolean;
  player_status: string;
}

export interface FreeAgentPlayer {
  id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility?: string[] | null;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  pitcher_role?: string | null;
  player_status?: string;
}

/** Normalize position to roster-limit key */
function normalizePosition(posAbbr: string, pitcherRole?: string | null): string {
  let pos = posAbbr;
  if (pos === 'P' && pitcherRole) pos = pitcherRole;
  else if (pos === 'P') pos = 'SP';
  if (['LF', 'CF', 'RF'].includes(pos)) pos = 'OF';
  if (pos === 'TWP') pos = 'UTIL';
  return pos;
}

const DEFAULT_STARTER_SLOT_ORDER = ['C', '1B', '2B', 'SS', '3B', 'OF1', 'OF2', 'OF3', 'DH', 'UTIL', 'SP1', 'SP2', 'RP1', 'RP2'] as const;

/** Build starter slots dynamically from league config (mirrors MLBBetaMyTeam logic) */
function buildStarterSlotOrder(config: any): string[] {
  if (!config?.roster_mins) return [...DEFAULT_STARTER_SLOT_ORDER];
  const mins = config.roster_mins as Record<string, number>;
  const pitcherType = config.pitcher_slot_type || 'split';
  const slots: string[] = [];
  const posOrder = ['C', '1B', '2B', 'SS', '3B', 'OF', 'DH', 'UTIL'];
  for (const pos of posOrder) {
    const count = mins[pos] ?? 0;
    if (count <= 0) continue;
    if (count === 1) slots.push(pos);
    else for (let i = 1; i <= count; i++) slots.push(`${pos}${i}`);
  }
  if (pitcherType === 'combined') {
    const pCount = mins['P'] ?? 0;
    if (pCount === 1) slots.push('P');
    else for (let i = 1; i <= pCount; i++) slots.push(`P${i}`);
  } else {
    const spCount = mins['SP'] ?? 0;
    const rpCount = mins['RP'] ?? 0;
    if (spCount === 1) slots.push('SP'); else for (let i = 1; i <= spCount; i++) slots.push(`SP${i}`);
    if (rpCount === 1) slots.push('RP'); else for (let i = 1; i <= rpCount; i++) slots.push(`RP${i}`);
  }
  return slots.length > 0 ? slots : [...DEFAULT_STARTER_SLOT_ORDER];
}

function buildSlotCapacity(slotOrder: string[]): Record<string, number> {
  const cap: Record<string, number> = {};
  for (const s of slotOrder) cap[s] = 1;
  return cap;
}

const HITTER_POSITIONS = new Set(['C', '1B', '2B', '3B', 'SS', 'OF', 'LF', 'CF', 'RF', 'DH']);

function normalizeRosterSlot(rawSlot: string): string {
  return (rawSlot || 'BENCH').toUpperCase().trim();
}

/** Map a single normalized position to the starter slots it can occupy */
function slotsForPosition(pos: string, starterSlots: string[], pitcherType: string): string[] {
  switch (pos) {
    case 'C': return ['C', 'DH', 'UTIL'];
    case '1B': return ['1B', 'DH', 'UTIL'];
    case '2B': return ['2B', 'DH', 'UTIL'];
    case 'SS': return ['SS', 'DH', 'UTIL'];
    case '3B': return ['3B', 'DH', 'UTIL'];
    case 'OF': return starterSlots.filter(s => s.startsWith('OF')).concat(['DH', 'UTIL']);
    case 'DH': return ['DH', 'UTIL'];
    case 'SP':
    case 'RP':
      if (pitcherType === 'combined') {
        return starterSlots.filter(s => s === 'P' || s.startsWith('P') && /^P\d+$/.test(s));
      }
      return pos === 'SP'
        ? starterSlots.filter(s => s === 'SP' || s.startsWith('SP') && /^SP\d+$/.test(s))
        : starterSlots.filter(s => s === 'RP' || s.startsWith('RP') && /^RP\d+$/.test(s));
    default:
      // Unknown pitcher position — try combined P slots or split SP/RP
      if (pitcherType === 'combined') {
        return starterSlots.filter(s => s === 'P' || /^P\d+$/.test(s));
      }
      return starterSlots.filter(s => s === 'SP' || /^SP\d+$/.test(s) || s === 'RP' || /^RP\d+$/.test(s));
  }
}

function getStarterCandidates(rawSlot: string, normalizedPos: string, starterSlots: string[], pitcherType: string, eligiblePositions?: string[]): string[] {
  const current = normalizeRosterSlot(rawSlot);
  const fromCurrent =
    current === 'OF' ? starterSlots.filter(s => s.startsWith('OF')) :
    current === 'SP' ? starterSlots.filter(s => s === 'SP' || /^SP\d+$/.test(s)) :
    current === 'RP' ? starterSlots.filter(s => s === 'RP' || /^RP\d+$/.test(s)) :
    current === 'P' ? starterSlots.filter(s => s === 'P' || /^P\d+$/.test(s)) :
    starterSlots.includes(current) ? [current] : [];

  const fromPosition = slotsForPosition(normalizedPos, starterSlots, pitcherType);

  // Include slots from eligible positions (position_eligibility)
  const fromEligible: string[] = [];
  if (eligiblePositions) {
    for (const ePos of eligiblePositions) {
      const norm = normalizePosition(ePos);
      if (norm !== normalizedPos) {
        fromEligible.push(...slotsForPosition(norm, starterSlots, pitcherType));
      }
    }
  }

  // Current-slot-first preserves intentional user swaps; position/eligible are fallbacks
  return Array.from(new Set([...fromCurrent, ...fromPosition, ...fromEligible]));
}

function canOccupySlot(normalizedPos: string, slot: string, pitcherType: string, eligiblePositions?: string[]): boolean {
  if (canOccupySlotSingle(normalizedPos, slot, pitcherType)) return true;
  if (eligiblePositions) {
    for (const ePos of eligiblePositions) {
      const norm = normalizePosition(ePos);
      if (canOccupySlotSingle(norm, slot, pitcherType)) return true;
    }
  }
  return false;
}

function canOccupySlotSingle(normalizedPos: string, slot: string, pitcherType: string): boolean {
  if (slot === 'DH' || slot === 'UTIL') return HITTER_POSITIONS.has(normalizedPos);
  if (/^OF\d*$/.test(slot)) return normalizedPos === 'OF';
  // Combined P slots: any pitcher (SP or RP) can fill P/P1/P2/etc.
  if (pitcherType === 'combined' && /^P\d*$/.test(slot)) {
    return normalizedPos === 'SP' || normalizedPos === 'RP';
  }
  // Split slots
  if (/^SP\d*$/.test(slot)) return normalizedPos === 'SP';
  if (/^RP\d*$/.test(slot)) return normalizedPos === 'RP';
  return normalizedPos === slot;
}

export type LineupLockType = 'weekly' | 'per_game';

/**
 * @param lineupDate  The date whose lineup is being edited (`YYYY-MM-DD`, ET), for
 *   per-game lock mode. Defaults to today.
 *
 *   This EXISTS because the locks were computed from `getTodayET()` while the
 *   lineup being edited was `viewDate`. Once today's games went final, every
 *   player whose team had played was reported locked for TOMORROW's lineup --
 *   which on 2026-09-11 was every rostered player on all 10 teams of the MLB
 *   Beta league, mid-playoffs. The lock module was correct; it was being asked
 *   about the wrong day.
 */
export const useAddDrop = (lineupDate?: string) => {
  const { activeLeague, teams } = useLeague();
  const { user } = useAuth();
  const { isActingAs, actAsTeamId, actAsLeagueId } = useCommissionerActAs();
  const { toast } = useToast();

  const [roster, setRoster] = useState<RosterPlayer[]>([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rosterLocked, setRosterLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState('');

  // Per-player lock map: player_id -> lock reason (or null if unlocked)
  const [playerLocks, setPlayerLocks] = useState<Map<string, string>>(new Map());
  const [lineupLockType, setLineupLockType] = useState<LineupLockType>('weekly');

  // Rostered player IDs across all league teams
  const [leagueRosteredIds, setLeagueRosteredIds] = useState<Set<string>>(new Set());
  // Map of player_id -> team_name for ownership display
  const [playerOwnership, setPlayerOwnership] = useState<Map<string, string>>(new Map());
  // Players currently on the waiver wire (recently dropped, active waiver window)
  const [waiverWirePlayerIds, setWaiverWirePlayerIds] = useState<Set<string>>(new Set());
  // Map of player_id -> waiver_expires_at for countdown timers
  const [waiverExpiry, setWaiverExpiry] = useState<Map<string, string>>(new Map());
  // Map of player_id -> waiver_wire.id for linking claims
  const [waiverWireIds, setWaiverWireIds] = useState<Map<string, string>>(new Map());

  // League category config for dynamic slot building
  const [categoryConfig, setCategoryConfig] = useState<any>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  // Roster limits from league config
  const [rosterLimits, setRosterLimits] = useState<Record<string, number>>({});
  const [totalRosterSize, setTotalRosterSize] = useState<number>(25);
  const [maxAcquisitions, setMaxAcquisitions] = useState<number>(0);
  const [acquisitionPeriod, setAcquisitionPeriod] = useState<'weekly' | 'season'>('weekly');
  const [acquisitionsUsed, setAcquisitionsUsed] = useState<number>(0);

  const myTeam = teams.find(t => t.user_id === user?.id);
  const faabRemaining = myTeam?.faab_remaining ?? activeLeague?.faab_budget ?? 0;

  // Load roster limits and lock type from league category_config
  useEffect(() => {
    if (!activeLeague) return;
    const loadLimits = async () => {
      const { data } = await supabase
        .from('leagues')
        .select('category_config')
        .eq('id', activeLeague.id)
        .single();

      const config = data?.category_config as any;
      setCategoryConfig(config);
      setConfigLoaded(true);
      if (config?.roster_limits) setRosterLimits(config.roster_limits);
      if (config?.total_roster_size) setTotalRosterSize(config.total_roster_size);
      if (config?.lineup_lock_type) setLineupLockType(config.lineup_lock_type);
      if (config?.max_acquisitions !== undefined) setMaxAcquisitions(config.max_acquisitions);
      if (config?.acquisition_period) setAcquisitionPeriod(config.acquisition_period);
    };
    loadLimits();
  }, [activeLeague]);

  // Fetch user's roster — wait for config so normalization uses correct slots
  const fetchRoster = useCallback(async () => {
    if (!myTeam) { setRosterLoading(false); return; }
    if (!configLoaded) return;

    const { data, error } = await supabase
      .from('roster_assignments')
      .select('id, player_id, slot, is_starter, players!inner(full_name, position_abbreviation, position_eligibility, mlb_team_abbreviation, mlb_team_id, mlb_id, headshot_url, status)')
      .eq('team_id', myTeam.id);

    if (error) {
      console.error('Failed to fetch roster:', error);
      setRosterLoading(false);
      return;
    }

    const mapped: RosterPlayer[] = (data || []).map((r: any) => ({
      assignment_id: r.id,
      player_id: r.player_id,
      full_name: r.players.full_name,
      position_abbreviation: r.players.position_abbreviation,
      position_eligibility: r.players.position_eligibility || null,
      mlb_team_abbreviation: r.players.mlb_team_abbreviation,
      mlb_team_id: r.players.mlb_team_id ?? null,
      mlb_id: r.players.mlb_id || null,
      headshot_url: r.players.headshot_url,
      slot: normalizeRosterSlot(r.slot),
      is_starter: r.is_starter,
      player_status: r.players.status || 'Active',
    }));

    // Build dynamic slot order from league config
    const starterSlots = buildStarterSlotOrder(categoryConfig);
    const slotCap = buildSlotCapacity(starterSlots);
    const pitcherType = categoryConfig?.pitcher_slot_type || 'split';

    // Normalize legacy/invalid starter slots, enforce hitter-only DH/UTIL,
    // and cap starters to valid starter slots.
    const used = new Map<string, number>();
    starterSlots.forEach((s) => used.set(s, 0));

    const normalized = mapped.map((p) => {
      if (p.slot === 'IL') {
        return { ...p, is_starter: false };
      }

      if (!p.is_starter) {
        if (p.slot === 'BENCH') return p;
        return { ...p, slot: 'BENCH', is_starter: false };
      }

      const normalizedPos = normalizePosition(p.position_abbreviation);
      const rawElig = p.position_eligibility;
      const eligiblePositions = Array.isArray(rawElig) ? (rawElig.length > 0 ? rawElig : undefined) : rawElig && typeof rawElig === 'object' ? Object.keys(rawElig) : undefined;
      const candidates = getStarterCandidates(p.slot, normalizedPos, starterSlots, pitcherType, eligiblePositions);

      for (const candidate of candidates) {
        if (!starterSlots.includes(candidate)) continue;
        if (!canOccupySlot(normalizedPos, candidate, pitcherType, eligiblePositions)) continue;
        const filled = used.get(candidate) || 0;
        const cap = slotCap[candidate] || 1;
        if (filled >= cap) continue;
        used.set(candidate, filled + 1);
        return { ...p, slot: candidate, is_starter: true };
      }

      return { ...p, slot: 'BENCH', is_starter: false };
    });

    // Normalization is display-only — never write back to DB.
    // DB writes happen only via explicit user actions (swap, optimize, add/drop).
    setRoster(normalized);
    setRosterLoading(false);
  }, [myTeam, categoryConfig, configLoaded]);

  // Fetch league-wide roster ownership
  const fetchLeagueOwnership = useCallback(async () => {
    if (!teams.length) return;

    const { data } = await supabase
      .from('roster_assignments')
      .select('player_id, team_id')
      .in('team_id', teams.map(t => t.id));

    const ids = new Set<string>();
    const ownership = new Map<string, string>();
    (data || []).forEach(r => {
      ids.add(r.player_id);
      const team = teams.find(t => t.id === r.team_id);
      if (team) ownership.set(r.player_id, team.name);
    });
    setLeagueRosteredIds(ids);
    setPlayerOwnership(ownership);
  }, [teams]);

  // Fetch players on the waiver wire (active waiver window, not yet cleared/claimed)
  const fetchWaiverWire = useCallback(async () => {
    if (!activeLeague) return;
    const { data } = await supabase
      .from('waiver_wire')
      .select('id, player_id, waiver_expires_at')
      .eq('league_id', activeLeague.id)
      .eq('status', 'active')
      .gt('waiver_expires_at', new Date().toISOString());
    const ids = new Set<string>();
    const expiry = new Map<string, string>();
    const wwIds = new Map<string, string>();
    (data || []).forEach(r => {
      ids.add(r.player_id);
      expiry.set(r.player_id, r.waiver_expires_at);
      wwIds.set(r.player_id, r.id);
    });
    setWaiverWirePlayerIds(ids);
    setWaiverExpiry(expiry);
    setWaiverWireIds(wwIds);
  }, [activeLeague]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);
  useEffect(() => { fetchLeagueOwnership(); }, [fetchLeagueOwnership]);
  useEffect(() => { fetchWaiverWire(); }, [fetchWaiverWire]);

  // Re-fetch roster when tab/app regains focus (prevents stale data on mobile)
  useEffect(() => {
    const onFocus = () => { fetchRoster(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchRoster(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchRoster]);

  // Count acquisitions used (add_drop from league_activity + won waiver_claims)
  useEffect(() => {
    const countAcquisitions = async () => {
      if (!activeLeague || !myTeam || maxAcquisitions === 0) {
        setAcquisitionsUsed(0);
        return;
      }

      // Build date filter for weekly mode
      let dateFilter: string | null = null;
      if (acquisitionPeriod === 'weekly') {
        const { data: matchup } = await supabase
          .from('matchups')
          .select('week_start, week_end')
          .eq('league_id', activeLeague.id)
          .or(`home_team_id.eq.${myTeam.id},away_team_id.eq.${myTeam.id}`)
          .eq('status', 'scheduled')
          .order('week', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (matchup) dateFilter = matchup.week_start;
      }

      // Count add/drop activities
      let activityQuery = supabase
        .from('league_activity')
        .select('id', { count: 'exact', head: true })
        .eq('league_id', activeLeague.id)
        .eq('team_id', myTeam.id)
        .eq('activity_type', 'add_drop');
      if (dateFilter) activityQuery = activityQuery.gte('created_at', dateFilter);
      const { count: addDropCount } = await activityQuery;

      // Count won waiver claims
      let waiverQuery = supabase
        .from('waiver_claims')
        .select('id', { count: 'exact', head: true })
        .eq('league_id', activeLeague.id)
        .eq('team_id', myTeam.id)
        .eq('status', 'won');
      if (dateFilter) waiverQuery = waiverQuery.gte('processed_at', dateFilter);
      const { count: waiverCount } = await waiverQuery;

      setAcquisitionsUsed((addDropCount || 0) + (waiverCount || 0));
    };
    countAcquisitions();
  }, [activeLeague, myTeam, maxAcquisitions, acquisitionPeriod, roster]);

  // Roster lock check — supports both weekly and per-game modes
  useEffect(() => {
    const checkRosterLock = async () => {
      if (!activeLeague || !myTeam) return;

      // Find the current or next matchup week (scheduled OR live)
      const { data: matchup } = await supabase
        .from('matchups')
        .select('week_start, week_end, status')
        .eq('league_id', activeLeague.id)
        .or(`home_team_id.eq.${myTeam.id},away_team_id.eq.${myTeam.id}`)
        .in('status', ['scheduled', 'live'])
        .order('week', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (!matchup) return;

      if (lineupLockType === 'per_game') {
        // Per-game lock: check each rostered player's MLB team against today's games
        await checkPerGameLocks(matchup.week_start, matchup.week_end);
      } else {
        // Weekly lock: Monday 11:00 AM ET (16:00 UTC) each week.
        // Find the Monday on or after week_start as the lock day.
        const ws = new Date(matchup.week_start + 'T00:00:00');
        const dayOfWeek = ws.getUTCDay(); // 0=Sun .. 6=Sat
        const daysUntilMon = dayOfWeek <= 1 ? (1 - dayOfWeek) : (8 - dayOfWeek);
        const lockDate = new Date(ws);
        lockDate.setUTCDate(lockDate.getUTCDate() + daysUntilMon);
        // Lock time: 16:00 UTC (11:00 AM ET)
        const lockTime = new Date(Date.UTC(
          lockDate.getUTCFullYear(), lockDate.getUTCMonth(), lockDate.getUTCDate(),
          16, 0, 0,
        ));

        // Do not lock before Mar 30 2026 (Week 1 is unlocked)
        const firstLockDate = new Date(Date.UTC(2026, 2, 30, 16, 0, 0)); // Mar 30 2026 16:00 UTC
        if (lockTime < firstLockDate) {
          // Week 1 — no lock
          setRosterLocked(false);
          setLockMessage('');
        } else if (new Date() >= lockTime) {
          setRosterLocked(true);
          setLockMessage(`Rosters locked: Monday ${lockTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 11:00 AM ET`);
        }
      }
    };

    checkRosterLock();

    // Per-game mode: refresh locks every 60s so the UI updates as games start
    if (lineupLockType === 'per_game') {
      const interval = setInterval(checkRosterLock, 60_000);
      return () => clearInterval(interval);
    }
  }, [activeLeague, myTeam, lineupLockType, roster, lineupDate]);

  /** Per-game lock: check each player's MLB team for games that have started */
  const checkPerGameLocks = async (_weekStart: string, _weekEnd: string) => {
    if (!roster.length) return;

    // Lock against the date whose lineup is being EDITED, not against today.
    // Past days' games must not lock players for a future lineup, and today's
    // finished games must not lock tomorrow's.
    const targetDate = lineupDate ?? getTodayET();

    // SAB-751: ONE query, NO status filter. The old code ran two status-filtered
    // queries, which made the filter fail-open by construction: a status matching
    // neither `.in('In Progress','Final','Game Over')` nor `.eq('scheduled')` was
    // never FETCHED, so it could never be treated as started. And prod stores
    // lowercase `live`/`final`/`scheduled`/`postponed`, so the first filter
    // matched NOTHING and the lock never fired at all — 302 rostered players sat
    // in live games unlocked on 2026-08-20.
    //
    // Fetch every game for the date (bounded: ~15 rows/day) and classify in
    // src/lib/mlbLineupLocks.ts, where an unrecognised status can be made to mean
    // LOCKED rather than invisible.
    //
    // The `players .in('mlb_team_abbreviation', teamAbbrs)` lookup that used to
    // translate abbreviation -> team name is also gone: it matched 4,800-6,300
    // rows against PostgREST's 1,000-row cap, dropping whole teams. We compare
    // team ids the roster already carries.
    const { data: games } = await supabase
      .from('game_schedule')
      .select('home_team_id, away_team_id, start_time, status')
      .eq('game_date', targetDate);

    const now = new Date();
    const lockedTeamIds = collectLockedTeamIds(games || [], now);
    const locks = computePlayerLocks(roster, lockedTeamIds);

    setPlayerLocks(locks);
    // Don't set global rosterLocked for per-game mode
    setRosterLocked(false);
    setLockMessage('');
  };

  /** Re-check per-game locks on demand (call before swaps for freshest data) */
  const refreshPerGameLocks = async (): Promise<Map<string, string>> => {
    if (lineupLockType !== 'per_game' || !roster.length) return playerLocks;
    await checkPerGameLocks('', '');
    // Return the latest locks (state may not have flushed yet, so re-derive)
    const targetDate = lineupDate ?? getTodayET();
    // SAB-751: no abbreviation gate — a roster whose teams cannot be resolved must
    // still reach the fail-closed computation, not early-return with zero locks.
    if (!roster.length) return new Map();

    // SAB-751: THIS is the copy that actually gates swaps — MLBBetaMyTeam calls
    // refreshPerGameLocks before every move — so its fail-open behaviour was the
    // exploitable one. One unfiltered query, classification in the tested module.
    // See checkPerGameLocks above for the full reasoning.
    const { data: games } = await supabase
      .from('game_schedule')
      .select('home_team_id, away_team_id, start_time, status')
      .eq('game_date', targetDate);

    const now = new Date();
    const lockedTeamIds = collectLockedTeamIds(games || [], now);
    const locks = computePlayerLocks(roster, lockedTeamIds);
    setPlayerLocks(locks);
    return locks;
  };

  /** Check if a specific player is locked (per-game mode) */
  const isPlayerLocked = (playerId: string): boolean => {
    if (lineupLockType === 'weekly') return rosterLocked;
    return playerLocks.has(playerId);
  };

  /** Get lock reason for a specific player */
  const getPlayerLockReason = (playerId: string): string | null => {
    if (lineupLockType === 'weekly') return rosterLocked ? lockMessage : null;
    return playerLocks.get(playerId) || null;
  };

  /** Check if adding a player would violate per-position limits. */
  const checkPositionLimit = (addPlayer: FreeAgentPlayer, dropPlayer: RosterPlayer): string | null => {
    const pitcherSlotType = categoryConfig?.pitcher_slot_type || 'sp_rp';
    // In combined pitcher mode, all pitchers count as 'P' — no SP/RP split
    const normPos = (posAbbr: string, pitcherRole?: string | null) => {
      if (pitcherSlotType === 'combined' && (posAbbr === 'P' || posAbbr === 'SP' || posAbbr === 'RP')) return 'P';
      return normalizePosition(posAbbr, pitcherRole);
    };

    const addPos = normPos(addPlayer.position_abbreviation, addPlayer.pitcher_role);
    const dropPos = normPos(dropPlayer.position_abbreviation);

    if (addPos === dropPos) return null;

    const currentAtPos = roster.filter(p => {
      if (p.player_id === dropPlayer.player_id) return false;
      return normPos(p.position_abbreviation) === addPos;
    }).length;

    const limit = rosterLimits[addPos];
    if (limit !== undefined && limit > 0 && currentAtPos >= limit) {
      return `You already have ${currentAtPos} ${addPos} players (max ${limit}). Drop a ${addPos} player first.`;
    }

    return null;
  };

  /** Check if the added player can play the dropped player's slot.
   *  Returns a warning string if the player will be placed on BENCH instead. */
  const checkSlotEligibility = (addPlayer: FreeAgentPlayer, dropPlayer: RosterPlayer): string | null => {
    if (dropPlayer.slot === 'BENCH' || dropPlayer.slot === 'IL') return null;
    if (canPlaySlot(addPlayer.position_abbreviation, dropPlayer.slot, addPlayer.position_eligibility)) return null;
    // SAB-279 — show full eligibility (e.g. "C/1B") in the warning, not just primary position,
    // so multi-position players who legitimately can't fill the dropped slot get an accurate label.
    const positionDisplay = formatPositionDisplay(addPlayer.position_abbreviation, addPlayer.position_eligibility);
    return `${addPlayer.full_name} (${positionDisplay}) can't play ${dropPlayer.slot.replace(/\d+$/, '')}. They'll be placed on BENCH instead.`;
  };

  // Execute add/drop transaction
  const executeAddDrop = async (
    addPlayer: FreeAgentPlayer,
    dropPlayer: RosterPlayer,
    faabBid: number,
  ) => {
    if (!myTeam || !activeLeague) return false;

    // Check weekly roster lock
    if (lineupLockType === 'weekly' && rosterLocked) {
      toast({ title: 'Roster locked', description: lockMessage || 'Rosters are locked for this week.', variant: 'destructive' });
      return false;
    }

    // Check acquisition limit
    if (maxAcquisitions > 0 && acquisitionsUsed >= maxAcquisitions) {
      toast({
        title: 'Acquisition limit reached',
        description: `You've used ${acquisitionsUsed} of ${maxAcquisitions} allowed acquisitions ${acquisitionPeriod === 'weekly' ? 'this week' : 'this season'}.`,
        variant: 'destructive',
      });
      return false;
    }

    if (faabBid < 0) {
      toast({ title: 'Invalid bid', description: 'FAAB bid cannot be negative.', variant: 'destructive' });
      return false;
    }
    if (faabBid > faabRemaining) {
      toast({ title: 'Insufficient FAAB', description: `You only have $${faabRemaining} remaining.`, variant: 'destructive' });
      return false;
    }

    const limitViolation = checkPositionLimit(addPlayer, dropPlayer);
    if (limitViolation) {
      toast({ title: 'Position limit reached', description: limitViolation, variant: 'destructive' });
      return false;
    }

    setProcessing(true);

    // Determine target slot: use dropped player's slot if new player is eligible, else BENCH
    const targetSlot = canPlaySlot(addPlayer.position_abbreviation, dropPlayer.slot, addPlayer.position_eligibility)
      ? dropPlayer.slot
      : 'BENCH';
    const targetIsStarter = targetSlot === dropPlayer.slot ? dropPlayer.is_starter : false;

    // Atomic add/drop: delete + insert in one transaction to prevent race conditions
    const { data: rpcResult, error: rpcErr } = await supabase.rpc('add_drop_atomic', {
      p_team_id: myTeam.id,
      p_league_id: activeLeague.id,
      p_add_player_id: addPlayer.id,
      p_drop_assignment_id: dropPlayer.assignment_id,
      p_drop_player_id: dropPlayer.player_id,
      p_add_position: addPlayer.position_abbreviation,
      // Normalize eligibility to a real array before it hits the text[] RPC param.
      // players.position_eligibility can be a malformed {} object (JSONB default);
      // passing that raw fails PostgREST coercion with "expected JSON array".
      // toArray({}) -> [] (RPC then BENCH-falls-back on no match; primary position
      // still auto-fills via p_add_position). Matches the roster path's pattern.
      p_add_position_eligibility: toArray(addPlayer.position_eligibility),
      p_target_slot: targetSlot,
      p_target_is_starter: targetIsStarter,
      p_faab_bid: faabBid,
      p_faab_remaining: faabRemaining,
    });

    if (rpcErr || !rpcResult?.success) {
      toast({ title: 'Transaction failed', description: rpcResult?.error || rpcErr?.message || 'Unknown error', variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    const finalSlot = rpcResult.final_slot;
    const finalIsStarter = rpcResult.is_starter;

    // Daily lineup cleanup for dropped player (per_game mode)
    if (lineupLockType === 'per_game') {
      const today = getTodayET();
      // Delete future daily_lineups rows (not today's locked rows — those stats still count)
      await supabase
        .from('daily_lineups')
        .delete()
        .eq('team_id', myTeam.id)
        .eq('player_id', dropPlayer.player_id)
        .gt('game_date', today);
      // Delete today's row only if not locked
      await supabase
        .from('daily_lineups')
        .delete()
        .eq('team_id', myTeam.id)
        .eq('player_id', dropPlayer.player_id)
        .eq('game_date', today)
        .is('locked_at', null);
    }

    // Sync daily_lineups: add new player with validated slot (per_game mode)
    if (lineupLockType === 'per_game') {
      const today = getTodayET();
      await supabase
        .from('daily_lineups')
        .upsert({
          team_id: myTeam.id,
          player_id: addPlayer.id,
          game_date: today,
          slot: finalSlot,
          is_starter: finalIsStarter,
          locked_at: null,
        }, { onConflict: 'team_id,player_id,game_date' });
    }

    await supabase.from('league_activity').insert({
      league_id: activeLeague.id,
      team_id: myTeam.id,
      activity_type: 'add_drop',
      player_name: addPlayer.full_name,
      details: {
        added_player: addPlayer.full_name,
        dropped_player: dropPlayer.full_name,
        faab_cost: faabBid,
        slot: finalSlot,
        slot_changed: rpcResult.slot_changed,
      },
    } as any);

    // Log lineup_change if added player inherits a starter slot
    if (finalIsStarter) {
      supabase.from('roster_transactions').insert({
        league_id: activeLeague.id, team_id: myTeam.id, player_id: addPlayer.id,
        transaction_type: 'lineup_change', slot: finalSlot,
        meta: { old_is_starter: false, new_is_starter: true, trigger: 'add_drop_inherit' },
      }).then(() => {});
    }

    trackEvent('player_drafted', { player_name: addPlayer.full_name, player_position: addPlayer.position_abbreviation || '', player_team: addPlayer.mlb_team_abbreviation || '' });
    trackEvent('player_dropped', { player_name: dropPlayer.full_name, player_position: dropPlayer.position_abbreviation || '', player_team: dropPlayer.mlb_team_abbreviation || '' });

    const slotNote = rpcResult.slot_changed ? ` (placed on BENCH, not eligible for ${dropPlayer.slot})` : '';
    toast({
      title: 'Transaction complete',
      description: `Added ${addPlayer.full_name}, dropped ${dropPlayer.full_name}${faabBid > 0 ? ` ($${faabBid} FAAB)` : ''}${slotNote}`,
    });

    await fetchRoster();
    await fetchLeagueOwnership();
    setProcessing(false);
    return true;
  };

  // IL slot availability
  const maxILSlots = (categoryConfig?.roster_mins as Record<string, number>)?.['IL'] ?? 2;
  const usedILSlots = roster.filter(p => p.slot === 'IL').length;
  const availableILSlots = Math.max(0, maxILSlots - usedILSlots);

  // Active roster open slots: IL players don't count against the active roster limit
  const activePlayerCount = roster.filter(p => p.slot !== 'IL').length;
  const openActiveSlots = Math.max(0, totalRosterSize - activePlayerCount);

  /** Check if a free agent player is IL-eligible based on their status */
  const isFreeAgentILEligible = (player: FreeAgentPlayer): boolean => {
    return !!player.player_status && isILEligibleStatus(player.player_status);
  };

  /** Add a free agent directly to an IL slot without dropping anyone.
   *  Only works when the player is IL-eligible and there's an open IL slot. */
  const executeAddToIL = async (
    addPlayer: FreeAgentPlayer,
    faabBid: number,
  ): Promise<boolean> => {
    if (!myTeam || !activeLeague) return false;

    if (!addPlayer.player_status || !isILEligibleStatus(addPlayer.player_status)) {
      toast({ title: 'Not IL-eligible', description: `${addPlayer.full_name} does not have an IL-eligible status.`, variant: 'destructive' });
      return false;
    }

    if (availableILSlots <= 0) {
      toast({ title: 'No IL slots available', description: `All ${maxILSlots} IL slots are occupied. Move a player off IL first.`, variant: 'destructive' });
      return false;
    }

    if (maxAcquisitions > 0 && acquisitionsUsed >= maxAcquisitions) {
      toast({
        title: 'Acquisition limit reached',
        description: `You've used ${acquisitionsUsed} of ${maxAcquisitions} allowed acquisitions ${acquisitionPeriod === 'weekly' ? 'this week' : 'this season'}.`,
        variant: 'destructive',
      });
      return false;
    }

    if (faabBid < 0) {
      toast({ title: 'Invalid bid', description: 'FAAB bid cannot be negative.', variant: 'destructive' });
      return false;
    }
    if (faabBid > faabRemaining) {
      toast({ title: 'Insufficient FAAB', description: `You only have $${faabRemaining} remaining.`, variant: 'destructive' });
      return false;
    }

    setProcessing(true);

    const { error: addErr } = await supabase
      .from('roster_assignments')
      .insert({
        team_id: myTeam.id,
        player_id: addPlayer.id,
        slot: 'IL',
        is_starter: false,
        acquired_via: 'free_agent',
      });

    if (addErr) {
      toast({ title: 'Add failed', description: addErr.message, variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    // Sync daily_lineups: add IL player so ensure doesn't leave a ghost gap (per_game mode)
    if (lineupLockType === 'per_game') {
      const today = getTodayET();
      await supabase
        .from('daily_lineups')
        .upsert({
          team_id: myTeam.id,
          player_id: addPlayer.id,
          game_date: today,
          slot: 'IL',
          is_starter: false,
          locked_at: null,
        }, { onConflict: 'team_id,player_id,game_date' });
    }

    if (faabBid > 0) {
      await supabase
        .from('teams')
        .update({ faab_remaining: faabRemaining - faabBid })
        .eq('id', myTeam.id);
    }

    await supabase.from('roster_transactions').insert({
      league_id: activeLeague.id, team_id: myTeam.id, player_id: addPlayer.id,
      transaction_type: 'add', slot: 'IL', meta: { faab_cost: faabBid, direct_to_il: true },
    });

    trackEvent('player_drafted', { player_name: addPlayer.full_name, player_position: addPlayer.position_abbreviation || '', player_team: addPlayer.mlb_team_abbreviation || '' });

    toast({
      title: 'Added to IL',
      description: `${addPlayer.full_name} placed on Injured List${faabBid > 0 ? ` ($${faabBid} FAAB)` : ''}`,
    });

    await fetchRoster();
    await fetchLeagueOwnership();
    setProcessing(false);
    return true;
  };

  /** Add a free agent to BENCH without dropping anyone (when an active roster slot is open). */
  const executeAdd = async (
    addPlayer: FreeAgentPlayer,
    faabBid: number,
  ): Promise<boolean> => {
    if (!myTeam || !activeLeague) return false;

    if (lineupLockType === 'weekly' && rosterLocked) {
      toast({ title: 'Roster locked', description: lockMessage || 'Rosters are locked for this week.', variant: 'destructive' });
      return false;
    }

    if (openActiveSlots <= 0) {
      toast({ title: 'Roster full', description: 'No open active roster spots. Drop a player first.', variant: 'destructive' });
      return false;
    }

    if (maxAcquisitions > 0 && acquisitionsUsed >= maxAcquisitions) {
      toast({
        title: 'Acquisition limit reached',
        description: `You've used ${acquisitionsUsed} of ${maxAcquisitions} allowed acquisitions ${acquisitionPeriod === 'weekly' ? 'this week' : 'this season'}.`,
        variant: 'destructive',
      });
      return false;
    }

    if (faabBid < 0) {
      toast({ title: 'Invalid bid', description: 'FAAB bid cannot be negative.', variant: 'destructive' });
      return false;
    }
    if (faabBid > faabRemaining) {
      toast({ title: 'Insufficient FAAB', description: `You only have $${faabRemaining} remaining.`, variant: 'destructive' });
      return false;
    }

    setProcessing(true);

    // Re-verify active roster count server-side to prevent stale-state bypass
    const { count: freshActiveCount } = await supabase
      .from('roster_assignments')
      .select('id', { count: 'exact', head: true })
      .eq('team_id', myTeam.id)
      .neq('slot', 'IL');

    if (freshActiveCount !== null && freshActiveCount >= totalRosterSize) {
      toast({
        title: 'Roster full',
        description: `Your active roster is full (${freshActiveCount}/${totalRosterSize}). Drop a player first.`,
        variant: 'destructive',
      });
      setProcessing(false);
      return false;
    }

    const { error: addErr } = await supabase
      .from('roster_assignments')
      .insert({
        team_id: myTeam.id,
        player_id: addPlayer.id,
        slot: 'BENCH',
        is_starter: false,
        acquired_via: 'free_agent',
      });

    if (addErr) {
      toast({ title: 'Add failed', description: addErr.message, variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    // Auto-populate into daily_lineups as BENCH for today (per_game mode)
    if (lineupLockType === 'per_game') {
      const today = getTodayET();
      await supabase
        .from('daily_lineups')
        .upsert({
          team_id: myTeam.id,
          player_id: addPlayer.id,
          game_date: today,
          slot: 'BENCH',
          is_starter: false,
          locked_at: null,
        }, { onConflict: 'team_id,player_id,game_date' });
    }

    if (faabBid > 0) {
      await supabase
        .from('teams')
        .update({ faab_remaining: faabRemaining - faabBid })
        .eq('id', myTeam.id);
    }

    await supabase.from('league_activity').insert({
      league_id: activeLeague.id,
      team_id: myTeam.id,
      activity_type: 'add',
      player_name: addPlayer.full_name,
      details: {
        added_player: addPlayer.full_name,
        slot: 'BENCH',
        faab_cost: faabBid,
      },
    } as any);

    await supabase.from('roster_transactions').insert({
      league_id: activeLeague.id, team_id: myTeam.id, player_id: addPlayer.id,
      transaction_type: 'add', slot: 'BENCH', meta: { faab_cost: faabBid },
    });

    trackEvent('player_drafted', { player_name: addPlayer.full_name, player_position: addPlayer.position_abbreviation || '', player_team: addPlayer.mlb_team_abbreviation || '' });

    toast({
      title: 'Player added',
      description: `${addPlayer.full_name} added to bench${faabBid > 0 ? ` ($${faabBid} FAAB)` : ''}`,
    });

    await fetchRoster();
    await fetchLeagueOwnership();
    setProcessing(false);
    return true;
  };

  const isOnMyTeam = (playerId: string) => roster.some(p => p.player_id === playerId);
  const isFreeAgent = (playerId: string) => !leagueRosteredIds.has(playerId);
  const isOnWaiverWire = (playerId: string) => waiverWirePlayerIds.has(playerId);
  /** True free agent = not rostered AND not on active waiver wire */
  const isTrueFreeAgent = (playerId: string) => !leagueRosteredIds.has(playerId) && !waiverWirePlayerIds.has(playerId);
  const getOwnerTeamName = (playerId: string) => playerOwnership.get(playerId) || null;
  const getMyRosterPlayer = (playerId: string) => roster.find(p => p.player_id === playerId) || null;
  const getWaiverExpiry = (playerId: string) => waiverExpiry.get(playerId) || null;
  const getWaiverWireId = (playerId: string) => waiverWireIds.get(playerId) || null;

  const isILEligible = (player: RosterPlayer): boolean => {
    return isILEligibleStatus(player.player_status);
  };

  const moveToIL = async (player: RosterPlayer): Promise<boolean> => {
    if (!myTeam || !activeLeague) return false;

    if (!isILEligible(player)) {
      toast({
        title: 'Not IL-eligible',
        description: `${player.full_name} has status "${player.player_status}". Only players with an official injured/IL designation can be placed on IL.`,
        variant: 'destructive',
      });
      return false;
    }

    // Check IL slot availability
    if (availableILSlots <= 0) {
      toast({
        title: 'No IL slots available',
        description: `All ${maxILSlots} IL slot${maxILSlots === 1 ? '' : 's'} are occupied. Activate a player from IL first.`,
        variant: 'destructive',
      });
      return false;
    }

    setProcessing(true);
    const today = new Date().toISOString().slice(0, 10);

    console.log('[moveToIL] BEFORE:', { assignment_id: player.assignment_id, player_id: player.player_id, name: player.full_name, current_slot: player.slot });

    const { data: updated, error } = await supabase
      .from('roster_assignments')
      .update({ slot: 'IL', is_starter: false })
      .eq('id', player.assignment_id)
      .select('id, slot');

    console.log('[moveToIL] UPDATE result:', { updated, error });

    if (error) {
      toast({ title: 'Move to IL failed', description: error.message, variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    if (!updated || updated.length === 0) {
      toast({ title: 'Move to IL failed', description: 'No roster row was updated. Please try again.', variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    // Verify the update persisted
    const { data: verify } = await supabase
      .from('roster_assignments')
      .select('id, slot, is_starter')
      .eq('id', player.assignment_id)
      .single();
    console.log('[moveToIL] VERIFY after update:', verify);

    // Sync daily_lineups for per-game lock mode
    const { data: dlUpdated, error: dlError } = await supabase
      .from('daily_lineups')
      .update({ slot: 'IL', is_starter: false })
      .eq('team_id', myTeam.id)
      .eq('player_id', player.player_id)
      .eq('game_date', today)
      .select('id, slot');
    console.log('[moveToIL] daily_lineups sync:', { dlUpdated, dlError, today });

    await supabase.from('roster_transactions').insert({
      league_id: activeLeague.id, team_id: myTeam.id, player_id: player.player_id,
      transaction_type: 'il_move', slot: 'IL', meta: { previous_slot: player.slot },
    });

    toast({ title: 'Moved to IL', description: `${player.full_name} placed on Injured List` });
    await fetchRoster();

    // Log post-fetch state
    console.log('[moveToIL] POST-FETCH roster IL count:', roster.filter(p => p.slot === 'IL').length);

    setProcessing(false);
    return true;
  };

  const activateFromIL = async (player: RosterPlayer): Promise<boolean> => {
    if (!myTeam || !activeLeague) return false;

    // Check active roster limit before moving from IL to active
    if (openActiveSlots <= 0) {
      toast({
        title: 'Roster full',
        description: `Your active roster is full (${totalRosterSize}/${totalRosterSize}). Drop a player first to make room.`,
        variant: 'destructive',
      });
      return false;
    }

    setProcessing(true);
    const today = new Date().toISOString().slice(0, 10);

    // Re-verify active roster count server-side to prevent stale-state bypass
    const { count: freshActiveCount } = await supabase
      .from('roster_assignments')
      .select('id', { count: 'exact', head: true })
      .eq('team_id', myTeam.id)
      .neq('slot', 'IL');

    if (freshActiveCount !== null && freshActiveCount >= totalRosterSize) {
      toast({
        title: 'Roster full',
        description: `Your active roster is full (${freshActiveCount}/${totalRosterSize}). Drop a player first to make room.`,
        variant: 'destructive',
      });
      setProcessing(false);
      return false;
    }

    console.log('[activateFromIL] BEFORE:', { assignment_id: player.assignment_id, player_id: player.player_id, name: player.full_name, current_slot: player.slot });

    const { data: updated, error } = await supabase
      .from('roster_assignments')
      .update({ slot: 'BENCH', is_starter: false })
      .eq('id', player.assignment_id)
      .select('id, slot');

    console.log('[activateFromIL] UPDATE result:', { updated, error });

    if (error) {
      toast({ title: 'Activation failed', description: error.message, variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    if (!updated || updated.length === 0) {
      toast({ title: 'Activation failed', description: 'No roster row was updated. Please try again.', variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    // Verify the update persisted
    const { data: verify } = await supabase
      .from('roster_assignments')
      .select('id, slot, is_starter')
      .eq('id', player.assignment_id)
      .single();
    console.log('[activateFromIL] VERIFY after update:', verify);

    // Sync daily_lineups for per-game lock mode
    const { data: dlUpdated, error: dlError } = await supabase
      .from('daily_lineups')
      .update({ slot: 'BENCH', is_starter: false })
      .eq('team_id', myTeam.id)
      .eq('player_id', player.player_id)
      .eq('game_date', today)
      .select('id, slot');
    console.log('[activateFromIL] daily_lineups sync:', { dlUpdated, dlError, today });

    await supabase.from('roster_transactions').insert({
      league_id: activeLeague.id, team_id: myTeam.id, player_id: player.player_id,
      transaction_type: 'il_return', slot: 'BENCH',
    });

    toast({ title: 'Activated', description: `${player.full_name} moved to bench from IL` });
    await fetchRoster();
    setProcessing(false);
    return true;
  };

  /** Drop a player from the roster without adding a replacement. */
  const executeDrop = async (dropPlayer: RosterPlayer): Promise<boolean> => {
    if (!myTeam || !activeLeague) return false;

    // Commissioner-on-behalf path: route through the edge function so server-side
    // verifyCommissioner + lock check + new attribution columns + push notification
    // all fire. The edge fn handles roster_assignments, daily_lineups,
    // league_activity, and roster_transactions inserts itself, so we skip the
    // direct mutations below.
    if (isActingAs && actAsTeamId && actAsLeagueId === activeLeague.id) {
      setProcessing(true);
      const { data, error } = await supabase.functions.invoke('commissioner-override', {
        body: {
          action: 'force_drop',
          league_id: activeLeague.id,
          team_id: actAsTeamId,
          player_id: dropPlayer.player_id,
        },
      });
      if (error || (data as any)?.error) {
        const msg = error?.message || (data as any)?.error || 'Unknown error';
        toast({ title: 'Commissioner drop failed', description: msg, variant: 'destructive' });
        setProcessing(false);
        return false;
      }
      toast({ title: 'Player dropped', description: `${dropPlayer.full_name} dropped, owner notified` });
      setProcessing(false);
      return true;
    }

    if (lineupLockType === 'weekly' && rosterLocked) {
      toast({ title: 'Roster locked', description: lockMessage || 'Rosters are locked for this week.', variant: 'destructive' });
      return false;
    }

    setProcessing(true);

    const { error: dropErr } = await supabase
      .from('roster_assignments')
      .delete()
      .eq('id', dropPlayer.assignment_id);

    if (dropErr) {
      toast({ title: 'Drop failed', description: dropErr.message, variant: 'destructive' });
      setProcessing(false);
      return false;
    }

    // Daily lineup cleanup for dropped player (per_game mode)
    if (lineupLockType === 'per_game') {
      const today = getTodayET();
      await supabase
        .from('daily_lineups')
        .delete()
        .eq('team_id', myTeam.id)
        .eq('player_id', dropPlayer.player_id)
        .gt('game_date', today);
      await supabase
        .from('daily_lineups')
        .delete()
        .eq('team_id', myTeam.id)
        .eq('player_id', dropPlayer.player_id)
        .eq('game_date', today)
        .is('locked_at', null);
    }

    await supabase.from('league_activity').insert({
      league_id: activeLeague.id,
      team_id: myTeam.id,
      activity_type: 'drop',
      player_name: dropPlayer.full_name,
      details: {
        dropped_player: dropPlayer.full_name,
        previous_slot: dropPlayer.slot,
      },
    } as any);

    await supabase.from('roster_transactions').insert({
      league_id: activeLeague.id, team_id: myTeam.id, player_id: dropPlayer.player_id,
      transaction_type: 'drop_only', slot: dropPlayer.slot,
    });

    await supabase.from('waiver_wire').insert({
      league_id: activeLeague.id,
      player_id: dropPlayer.player_id,
      dropped_by_team_id: myTeam.id,
    } as any);

    trackEvent('player_dropped', { player_name: dropPlayer.full_name, player_position: dropPlayer.position_abbreviation || '', player_team: dropPlayer.mlb_team_abbreviation || '' });

    toast({
      title: 'Player dropped',
      description: `${dropPlayer.full_name} has been dropped from your roster`,
    });

    await fetchRoster();
    await fetchLeagueOwnership();
    setProcessing(false);
    return true;
  };

  const acquisitionsRemaining = maxAcquisitions > 0 ? Math.max(0, maxAcquisitions - acquisitionsUsed) : Infinity;
  const isAcquisitionLimitReached = maxAcquisitions > 0 && acquisitionsUsed >= maxAcquisitions;

  return {
    roster,
    rosterLoading,
    processing,
    rosterLocked,
    lockMessage,
    lineupLockType,
    playerLocks,
    isPlayerLocked,
    getPlayerLockReason,
    refreshPerGameLocks,
    myTeam,
    faabRemaining,
    rosterLimits,
    totalRosterSize,
    maxAcquisitions,
    acquisitionPeriod,
    acquisitionsUsed,
    acquisitionsRemaining,
    isAcquisitionLimitReached,
    checkPositionLimit,
    checkSlotEligibility,
    executeAddDrop,
    executeDrop,
    executeAdd,
    executeAddToIL,
    isFreeAgentILEligible,
    availableILSlots,
    openActiveSlots,
    maxILSlots,
    moveToIL,
    activateFromIL,
    isILEligible,
    isOnMyTeam,
    isFreeAgent,
    isTrueFreeAgent,
    isOnWaiverWire,
    getOwnerTeamName,
    getMyRosterPlayer,
    getWaiverExpiry,
    getWaiverWireId,
    fetchRoster,
  };
};
